import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import moment from "moment";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";


const initialValues = {
  name: "",
  slug: "",
  description: "",
  monthlyPrice: 29,
  originalPrice: 39,
  trialDays: 7,
  badgeText: "",
  buttonText: "Start Free Trial",
  features: [""],
  isFeatured: false,
  isPublic: true,
  isActive: true,
  sortOrder: 0,
};

const SubscriptionPlans = () => {
  const { request } = useRequest();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [syncFilter, setSyncFilter] = useState("all");

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (statusFilter !== "all") query.append("status", statusFilter);
      if (syncFilter !== "all") query.append("stripeSyncStatus", syncFilter);
      if (appliedSearch.trim()) query.append("q", appliedSearch.trim());

      await request({
        url: `${apiPath.adminPlans}${query.toString() ? `?${query.toString()}` : ""}`,
        method: "GET",
        onSuccess: (response) => {
          setPlans(Array.isArray(response?.data) ? response.data : []);
        },
        onError: (error) => {
          ShowToast(
            error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch plans",
            Severty.ERROR,
          );
        },
      });
    } finally {
      setLoading(false);
    }
  }, [request, appliedSearch, statusFilter, syncFilter]);

  // Add this useEffect
  useEffect(() => {
    if (modalOpen) {
      if (editingPlan) {
        form.setFieldsValue({
          name: editingPlan.name,
          slug: editingPlan.slug,
          description: editingPlan.description || "",
          monthlyPrice: editingPlan.monthlyPrice,
          originalPrice: editingPlan.originalPrice,
          trialDays: editingPlan.trialDays,
          badgeText: editingPlan.badgeText || "",
          buttonText: editingPlan.buttonText || "Start Free Trial",
          features: editingPlan.features?.length ? editingPlan.features : [""],
          isFeatured: editingPlan.isFeatured || false,
          isPublic:
            editingPlan.isPublic !== undefined ? editingPlan.isPublic : true,
          isActive:
            editingPlan.isActive !== undefined ? editingPlan.isActive : true,
          sortOrder: editingPlan.sortOrder || 0,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          ...initialValues,
          features: [""],
        });
      }
    }
  }, [modalOpen, editingPlan, form]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const openCreateModal = () => {
    setEditingPlan(null);
    setModalOpen(true);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setModalOpen(true);
  };
  const handleSubmit = async () => {
    try {
      const currentValues = form.getFieldsValue();
      const cleanedFeatures = (currentValues.features || []).filter(
        (feature) => feature && feature.trim() !== "",
      );

      // If no features, add one empty feature for validation
      const featuresToValidate =
        cleanedFeatures.length === 0 ? [""] : cleanedFeatures;

      // Update form with cleaned features for validation
      form.setFieldsValue({
        ...currentValues,
        features: featuresToValidate,
      });

      // Now validate
      const values = await form.validateFields();

      // Final cleanup: remove empty features for submission
      const finalFeatures = (values.features || []).filter(
        (feature) => feature && feature.trim() !== "",
      );

      if (finalFeatures.length === 0) {
        ShowToast("At least one feature is required", Severty.ERROR);
        return;
      }

      setSaving(true);

      const payload = {
        ...values,
        features: finalFeatures,
      };

      await request({
        url: editingPlan
          ? `${apiPath.adminPlans}/${editingPlan.id || editingPlan._id}`
          : apiPath.adminPlans,
        method: editingPlan ? "PUT" : "POST",
        data: payload,
        onSuccess: () => {
          ShowToast(
            editingPlan
              ? "Plan updated successfully"
              : "Plan created successfully",
            Severty.SUCCESS,
          );
          setModalOpen(false);
          form.resetFields(); // Reset form after successful submission
          fetchPlans();
        },
        onError: (error) => {
          ShowToast(
            error?.response?.data?.message ||
            error?.message ||
            "Failed to save plan",
            Severty.ERROR,
          );
        },
      });
    } catch (error) {
      console.error("Validation error:", error);
      if (error.errorFields) {
        ShowToast("Please fill in all required fields", Severty.ERROR);
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (plan, isActive) => {
    await request({
      url: `${apiPath.adminPlans}/${plan.id || plan._id}/status`,
      method: "PATCH",
      data: { isActive },
      onSuccess: () => {
        ShowToast("Plan status updated", Severty.SUCCESS);
        fetchPlans();
      },
      onError: (error) => {
        ShowToast(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update status",
          Severty.ERROR,
        );
      },
    });
  };

  const toggleFeatured = async (plan, isFeatured) => {
    await request({
      url: `${apiPath.adminPlans}/${plan.id || plan._id}/featured`,
      method: "PATCH",
      data: { isFeatured },
      onSuccess: () => {
        ShowToast("Featured plan updated", Severty.SUCCESS);
        fetchPlans();
      },
      onError: (error) => {
        ShowToast(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update featured plan",
          Severty.ERROR,
        );
      },
    });
  };

  const handleDelete = async (plan) => {
    await request({
      url: `${apiPath.adminPlans}/${plan.id || plan._id}`,
      method: "DELETE",
      data: {},
      onSuccess: () => {
        ShowToast("Plan deleted successfully", Severty.SUCCESS);
        fetchPlans();
      },
      onError: (error) => {
        ShowToast(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete plan",
          Severty.ERROR,
        );
      },
    });
  };

  const sortedPlans = useMemo(
    () =>
      [...plans].sort((a, b) => {
        if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        }
        return (
          new Date(a.createdAt || 0).getTime() -
          new Date(b.createdAt || 0).getTime()
        );
      }),
    [plans],
  );

  const reorderPlans = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= sortedPlans.length) return;
    const nextPlans = [...sortedPlans];
    const [moved] = nextPlans.splice(fromIndex, 1);
    nextPlans.splice(toIndex, 0, moved);
    const planIds = nextPlans.map((plan) => plan.id || plan._id);

    await request({
      url: `${apiPath.adminPlans}/reorder`,
      method: "POST",
      data: { planIds },
      onSuccess: () => {
        ShowToast("Plan order updated", Severty.SUCCESS);
        fetchPlans();
      },
      onError: (error) => {
        ShowToast(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to reorder plans",
          Severty.ERROR,
        );
      },
    });
  };

  const columns = [
    {
      title: "Plan Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Monthly Price",
      dataIndex: "monthlyPrice",
      key: "monthlyPrice",
      render: (value) => `£${Number(value || 0).toFixed(2)}`,
    },
    {
      title: "Original Price",
      dataIndex: "originalPrice",
      key: "originalPrice",
      render: (value) => (value ? `£${Number(value).toFixed(2)}` : "—"),
    },
    {
      title: "Trial Days",
      dataIndex: "trialDays",
      key: "trialDays",
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag color={record.isActive ? "green" : "red"}>
          {record.isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Featured",
      key: "featured",
      render: (_, record) => (
        <Tag color={record.isFeatured ? "gold" : "default"}>
          {record.isFeatured ? "Featured" : "No"}
        </Tag>
      ),
    },
    {
      title: "Stripe Linked",
      key: "stripe",
      render: (_, record) => (
        <Tag color={record.stripePriceId ? "blue" : "default"}>
          {record.stripePriceId ? "Linked" : "Not linked"}
        </Tag>
      ),
    },
    {
      title: "Stripe Sync",
      dataIndex: "stripeSyncStatus",
      key: "stripeSyncStatus",
      render: (value, record) => (
        <Space direction="vertical" size={2}>
          <Tag
            color={
              value === "synced"
                ? "green"
                : value === "failed"
                  ? "red"
                  : value === "skipped"
                    ? "default"
                    : "orange"
            }
          >
            {value || "pending"}
          </Tag>
          {record.stripeLastSyncedAt ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {moment(record.stripeLastSyncedAt).format("YYYY-MM-DD HH:mm")}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value) =>
        value ? moment(value).format("YYYY-MM-DD HH:mm") : "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record, index) => (
        <Space wrap>
          <Tooltip title="Edit Plan">
            <Button
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          {/* <Popconfirm
            title="Delete this plan?"
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Tooltip title="Delete Plan">
              <Button danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm> */}
          <Tooltip
            title={record.isActive ? "Deactivate Plan" : "Activate Plan"}
          >
            <Switch
              checked={record.isActive}
              onChange={(checked) => toggleStatus(record, checked)}
            />
          </Tooltip>
          {/* <Tooltip
            title={
              record.isFeatured
                ? "Remove Featured Plan"
                : "Mark as Featured Plan"
            }
          >
            <Switch
              checked={record.isFeatured}
              onChange={(checked) => toggleFeatured(record, checked)}
            />
          </Tooltip> */}
          {/* <Tooltip title="Move Up">
            <Button
              icon={<ArrowUpOutlined />}
              disabled={index === 0}
              onClick={() => reorderPlans(index, index - 1)}
            />
          </Tooltip>
          <Tooltip title="Move Down">
            <Button
              icon={<ArrowDownOutlined />}
              disabled={index === sortedPlans.length - 1}
              onClick={() => reorderPlans(index, index + 1)}
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  return (
    <Card
      bordered={false}
      className="criclebox"
      title="Subscription Plans"
      extra={
        !loading && plans.length === 0 && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
          >
            Add Plan
          </Button>
        )
      }
    >
      <Typography.Paragraph>
        Manage public subscription plan, pricing, one-time trial settings, and
        automatic Stripe sync.
      </Typography.Paragraph>

      {/* <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search plan name, slug, description"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onPressEnter={() => setAppliedSearch(search)}
          style={{ width: 280 }}
          allowClear
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 160 }}
          options={[
            { value: "all", label: "All status" },
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ]}
        />
        <Select
          value={syncFilter}
          onChange={setSyncFilter}
          style={{ width: 180 }}
          options={[
            { value: "all", label: "All sync states" },
            { value: "synced", label: "Synced" },
            { value: "failed", label: "Failed" },
            { value: "pending", label: "Pending" },
            { value: "skipped", label: "Skipped" },
          ]}
        />
        <Button onClick={() => setAppliedSearch(search)}>Apply</Button>
      </Space> */}

      {!loading && plans.length === 0 ? (
        <Alert
          message="No Subscription Plans Found"
          description={
            <span>
              No plans have been created yet.{" "}
              <Button type="link" style={{ padding: 0 }} onClick={openCreateModal}>
                Click here to add your first plan.
              </Button>
            </span>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Table
          rowKey={(record) => record.id || record._id}
          columns={columns}
          dataSource={sortedPlans}
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
        />
      )}

      <Modal
        open={modalOpen}
        title={editingPlan ? "Edit Plan" : "Add Plan"}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={saving}
        width={820}
        okText={editingPlan ? "Update Plan" : "Create Plan"}
      >
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter plan name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug"
            rules={[
              { required: true, message: "Please enter slug" },
              {
                pattern: /^[a-z0-9-]+$/,
                message: "Use lowercase letters, numbers, and hyphens only",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space size={16} style={{ display: "flex" }} wrap>
            <Form.Item
              name="monthlyPrice"
              label="Monthly Price (GBP)"
              rules={[
                { required: true, message: "Please enter monthly price" },
              ]}
            >
              <InputNumber min={0} precision={2} style={{ width: 180 }} />
            </Form.Item>
            <Form.Item name="originalPrice" label="Original Price (GBP)">
              <InputNumber min={0} precision={2} style={{ width: 180 }} />
            </Form.Item>
            <Form.Item
              name="trialDays"
              label="Trial Days"
              rules={[{ required: true, message: "Please enter trial days" }]}
            >
              <InputNumber min={0} max={365} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="sortOrder" label="Sort Order">
              <InputNumber style={{ width: 140 }} />
            </Form.Item>
          </Space>
          <Space size={16} style={{ display: "flex" }} wrap>
            <Form.Item name="badgeText" label="Badge Text">
              <Input style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="buttonText" label="Button Text">
              <Input style={{ width: 220 }} />
            </Form.Item>
          </Space>

          <Form.List name="features">
            {(fields, { add, remove }) => (
              <div>
                <Typography.Title level={5}>Features</Typography.Title>
                {fields.map((field, index) => (
                  <Space
                    key={field.key}
                    style={{ display: "flex", marginBottom: 8 }}
                  >
                    <Form.Item
                      {...field}
                      rules={[
                        {
                          required: true,
                          message: "Feature text is required",
                          // Only validate if the feature is not the last empty one
                          validator: (_, value) => {
                            if (!value || value.trim() === "") {
                              // Don't validate empty features if they're the only one
                              if (fields.length === 1 && index === 0) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                new Error("Feature text is required"),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0, width: 560 }}
                    >
                      <Input placeholder={`Feature ${index + 1}`} />
                    </Form.Item>
                    <Button onClick={() => remove(field.name)}>Remove</Button>
                  </Space>
                ))}
                <Button onClick={() => add("")}>Add Feature</Button>
              </div>
            )}
          </Form.List>

          <Space size={24} style={{ marginTop: 24 }}>
            <Form.Item
              name="isFeatured"
              label="Featured"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item name="isPublic" label="Public" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          {/* {editingPlan ? (
            <Card size="small" style={{ marginTop: 16 }}>
              <Space direction="vertical" size={4}>
                <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>Stripe Sync Details</Typography.Text>
                <div><Typography.Text strong>Product ID: </Typography.Text><Typography.Text>{editingPlan.stripeProductId || "Not created yet"}</Typography.Text></div>
                <div><Typography.Text strong>Price ID: </Typography.Text><Typography.Text>{editingPlan.stripePriceId || "Not created yet"}</Typography.Text></div>
                <div><Typography.Text strong>Sync Status: </Typography.Text><Tag color={editingPlan.stripeSyncStatus === "synced" ? "green" : "red"}>{editingPlan.stripeSyncStatus || "pending"}</Tag></div>
                {editingPlan.stripeLastError ? (
                  <div style={{ marginTop: 8 }}>
                    <Typography.Text type="danger" strong>Last Error: </Typography.Text>
                    <Typography.Text type="danger">{editingPlan.stripeLastError}</Typography.Text>
                  </div>
                ) : null}
              </Space>
            </Card>
          ) : null} */}
        </Form>
      </Modal>
    </Card>
  );
};

export default SubscriptionPlans;
